import type { RealEstateCase } from "../../../types.ts";
import { findMunicipality, normalizeLocationText } from "../location/locationSearch.ts";
import { inferComparableOrigin } from "./comparableLifecycle.ts";
import { normalizeRoomCount } from "../data/roomNormalization.ts";
import {
  canonicalCondition,
  canonicalPropertyType,
  effectiveApartmentArea,
  isPlausibleApartmentArea,
  isPlausibleMarketPrice,
  isPlausiblePricePerM2,
  normalizeSlovakLocationName
} from "./propertyMatching.ts";
import {
  MARKET_METHODOLOGY_VERSION,
  MARKET_FUNNEL_REJECTION_REASONS,
  SIMILARITY_FACTORS,
  type MarketAnalysis,
  type MarketAnalysisComparable,
  type MarketComparableDraft,
  type MarketDatasetSummary,
  type MarketFunnelRejectionReason,
  type MarketFunnelSnapshot,
  type MarketFunnelStage,
  type MarketPriceDistribution,
  type MarketPropertySnapshot,
  type MarketStrategy,
  type MarketSubjectSnapshot,
  type MarketValuationResult,
  type SimilarityScoreBreakdown
} from "./types.ts";

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const textValue = (value: unknown) => (typeof value === "string" ? value : "");

const positiveNumber = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) && value > 0 ? value : undefined;

const finiteNumber = (value: unknown, fallback = 0) =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

const dateValue = (value: unknown, fallback: string) => {
  if (typeof value !== "string" || !Number.isFinite(Date.parse(value))) {
    return fallback;
  }

  return value;
};

const propertySnapshot = (value: unknown): MarketPropertySnapshot => {
  const source = isRecord(value) ? value : {};
  const city = textValue(source.city);
  const municipality = findMunicipality(city);
  const street = textValue(source.street);
  const citySource = source.citySource === "dataset" || source.citySource === "custom"
    ? source.citySource
    : municipality?.name === city.trim()
      ? "dataset"
      : city
        ? "custom"
        : undefined;
  const location = isRecord(source.location) ? source.location : undefined;
  const areas = isRecord(source.areas) ? source.areas : undefined;
  const technical = isRecord(source.technical) ? source.technical : undefined;
  const equipment = isRecord(source.equipment) ? source.equipment : undefined;
  return {
    city,
    cityNormalized: textValue(source.cityNormalized) || normalizeLocationText(city),
    citySource,
    municipalityId: textValue(source.municipalityId) || undefined,
    locality: textValue(source.locality),
    street,
    streetNormalized: textValue(source.streetNormalized) || normalizeLocationText(street),
    streetSource: source.streetSource === "dataset" || source.streetSource === "custom"
      ? source.streetSource
      : street
        ? "custom"
        : undefined,
    streetId: textValue(source.streetId) || undefined,
    propertyType: textValue(source.propertyType) || "Iné",
    rooms: textValue(source.rooms),
    area: positiveNumber(source.area),
    condition: textValue(source.condition),
    reconstruction: textValue(source.reconstruction),
    electricity: textValue(source.electricity),
    coreType: textValue(source.coreType),
    kitchen: textValue(source.kitchen),
    balconyType: textValue(source.balconyType),
    cellar: textValue(source.cellar),
    floor: typeof source.floor === "number" && Number.isFinite(source.floor) ? source.floor : undefined,
    totalFloors: typeof source.totalFloors === "number" && Number.isFinite(source.totalFloors) ? source.totalFloors : undefined,
    elevator: textValue(source.elevator),
    orientation: textValue(source.orientation),
    buildingCondition: textValue(source.buildingCondition),
    buildingType: textValue(source.buildingType) || undefined,
    location: location ? {
      country: textValue(location.country) || undefined,
      region: textValue(location.region) || undefined,
      district: textValue(location.district) || undefined,
      municipality: textValue(location.municipality) || city,
      municipalityNormalized: textValue(location.municipalityNormalized) || normalizeSlovakLocationName(textValue(location.municipality) || city),
      municipalityId: textValue(location.municipalityId) || undefined,
      locality: textValue(location.locality) || undefined,
      microlocation: textValue(location.microlocation) || undefined,
      street: textValue(location.street) || undefined,
      building: textValue(location.building) || undefined,
      address: textValue(location.address) || undefined,
      postalCode: textValue(location.postalCode) || undefined,
      sourceLocationCode: textValue(location.sourceLocationCode) || undefined
    } : undefined,
    areas: areas ? {
      apartmentArea: positiveNumber(areas.apartmentArea),
      interiorArea: positiveNumber(areas.interiorArea),
      balconyArea: positiveNumber(areas.balconyArea),
      loggiaArea: positiveNumber(areas.loggiaArea),
      cellarArea: positiveNumber(areas.cellarArea),
      terraceArea: positiveNumber(areas.terraceArea),
      totalArea: positiveNumber(areas.totalArea),
      areaBasis: areas.areaBasis === "interior" || areas.areaBasis === "apartment" || areas.areaBasis === "total_only"
        ? areas.areaBasis : "legacy_unspecified"
    } : undefined,
    technical: technical ? {
      condition: canonicalCondition(textValue(technical.condition)),
      electrical: textValue(technical.electrical) || undefined,
      coreBathroom: textValue(technical.coreBathroom) || undefined,
      kitchen: textValue(technical.kitchen) || undefined,
      windows: textValue(technical.windows) || undefined,
      floors: textValue(technical.floors) || undefined,
      doors: textValue(technical.doors) || undefined,
      buildingCondition: textValue(technical.buildingCondition) || undefined
    } : undefined,
    equipment: equipment as MarketPropertySnapshot["equipment"],
    canonicalPropertyType: canonicalPropertyType(textValue(source.propertyType)),
    fieldProvenance: isRecord(source.fieldProvenance) ? source.fieldProvenance as MarketPropertySnapshot["fieldProvenance"] : undefined
  };
};

export const normalizeMarketSubject = (value: unknown): MarketSubjectSnapshot => {
  const source = isRecord(value) ? value : {};
  const snapshot = propertySnapshot(source);
  const apartmentAreaIsExplicit = Boolean(snapshot.fieldProvenance?.apartmentArea);
  const interiorAreaIsExplicit = Boolean(snapshot.fieldProvenance?.interiorArea);
  const legacyArea = snapshot.areas?.totalArea
    ?? snapshot.areas?.apartmentArea
    ?? snapshot.areas?.interiorArea
    ?? snapshot.area;
  const areas = apartmentAreaIsExplicit || interiorAreaIsExplicit
    ? snapshot.areas
    : legacyArea
      ? {
        ...snapshot.areas,
        apartmentArea: undefined,
        interiorArea: undefined,
        totalArea: legacyArea,
        areaBasis: "legacy_unspecified" as const
      }
      : snapshot.areas;
  return {
    ...snapshot,
    areas,
    transactionType: typeof source.transactionType === "string" ? source.transactionType as MarketSubjectSnapshot["transactionType"] : undefined,
    askingPriceAtAnalysis: positiveNumber(source.askingPriceAtAnalysis)
  };
};

const emptyBreakdown = (): SimilarityScoreBreakdown => ({
  methodologyVersion: MARKET_METHODOLOGY_VERSION,
  components: Object.fromEntries(
    SIMILARITY_FACTORS.map((factor) => [factor, { weight: 0, available: false }])
  ) as SimilarityScoreBreakdown["components"],
  availableWeight: 0,
  completeness: 0,
  completenessPenalty: 0.7,
  normalizedScore: 0,
  finalScore: 0,
  selectionScore: 0,
  valuationWeightScore: 0,
  dataConfidence: {
    score: 0,
    level: "low",
    reason: "Dátová dôvera nebola vyhodnotená.",
    knownAttributes: [],
    unknownAttributes: []
  },
  explanations: []
});

const normalizeBreakdown = (value: unknown): SimilarityScoreBreakdown => {
  if (!isRecord(value) || !isRecord(value.components)) {
    return emptyBreakdown();
  }

  const fallback = emptyBreakdown();
  const components = Object.fromEntries(
    SIMILARITY_FACTORS.map((factor) => {
      const component = isRecord(value.components) && isRecord(value.components[factor])
        ? value.components[factor] as UnknownRecord
        : {};
      return [factor, {
        weight: finiteNumber(component.weight),
        available: component.available === true,
        score: typeof component.score === "number" && Number.isFinite(component.score) ? component.score : undefined
      }];
    })
  ) as SimilarityScoreBreakdown["components"];
  const confidence = isRecord(value.dataConfidence) ? value.dataConfidence : {};
  const confidenceScore = Math.max(0, Math.min(100, finiteNumber(confidence.score)));

  return {
    ...fallback,
    methodologyVersion: textValue(value.methodologyVersion) || MARKET_METHODOLOGY_VERSION,
    components,
    availableWeight: finiteNumber(value.availableWeight),
    completeness: finiteNumber(value.completeness),
    completenessPenalty: finiteNumber(value.completenessPenalty, 0.7),
    normalizedScore: finiteNumber(value.normalizedScore),
    finalScore: finiteNumber(value.finalScore),
    selectionScore: finiteNumber(
      value.selectionScore,
      finiteNumber(value.finalScore) * 0.85 + confidenceScore * 0.15
    ),
    valuationWeightScore: finiteNumber(value.valuationWeightScore, finiteNumber(value.finalScore)),
    dataConfidence: {
      score: confidenceScore,
      level: confidence.level === "high" || confidence.level === "medium" ? confidence.level : "low",
      reason: textValue(confidence.reason) || "Dátová dôvera nebola vyhodnotená.",
      knownAttributes: Array.isArray(confidence.knownAttributes)
        ? confidence.knownAttributes.filter((item): item is string => typeof item === "string")
        : [],
      unknownAttributes: Array.isArray(confidence.unknownAttributes)
        ? confidence.unknownAttributes.filter((item): item is string => typeof item === "string")
        : []
    },
    explanations: Array.isArray(value.explanations)
      ? value.explanations.filter((item): item is string => typeof item === "string").slice(0, 8)
      : []
  };
};

const normalizeComparable = (value: unknown, analysisId: string, userId: string, index: number): MarketAnalysisComparable | null => {
  if (!isRecord(value) || typeof value.id !== "string") {
    return null;
  }

  const now = new Date().toISOString();
  const sourceSnapshot = isRecord(value.sourceSnapshot) ? value.sourceSnapshot : {};
  return {
    ...propertySnapshot(value),
    id: value.id,
    analysisId,
    userId,
    sortOrder: Math.max(0, Math.round(finiteNumber(value.sortOrder, index))),
    askingPrice: positiveNumber(value.askingPrice),
    realizedPrice: positiveNumber(value.realizedPrice),
    listingStatus: textValue(value.listingStatus),
    sourceName: textValue(value.sourceName),
    sourceUrl: textValue(value.sourceUrl),
    capturedAt: dateValue(value.capturedAt, now),
    sourceSnapshot,
    origin: inferComparableOrigin(value.origin, sourceSnapshot),
    marketRole: value.marketRole === "competition" ? "competition" : "primary",
    marketContextGroup: value.marketContextGroup === "same_condition" || value.marketContextGroup === "other_condition"
      ? value.marketContextGroup
      : undefined,
    primaryQualificationReasons: Array.isArray(value.primaryQualificationReasons)
      ? value.primaryQualificationReasons.filter((reason): reason is string => typeof reason === "string")
      : undefined,
    sourceHash: textValue(value.sourceHash),
    similarityScore: Math.max(0, Math.min(100, finiteNumber(value.similarityScore))),
    scoreBreakdown: normalizeBreakdown(value.scoreBreakdown),
    decision: value.decision === "excluded" ? "excluded" : "included",
    rejectionReason: typeof value.rejectionReason === "string"
      ? value.rejectionReason as MarketAnalysisComparable["rejectionReason"]
      : undefined,
    createdAt: dateValue(value.createdAt, now),
    updatedAt: dateValue(value.updatedAt, now)
  };
};

const normalizeDataset = (value: unknown): MarketDatasetSummary | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  return {
    count: Math.max(0, Math.round(finiteNumber(value.count))),
    medianPricePerM2: finiteNumber(value.medianPricePerM2),
    weightedPricePerM2: finiteNumber(value.weightedPricePerM2),
    low: finiteNumber(value.low),
    high: finiteNumber(value.high),
    dispersion: finiteNumber(value.dispersion)
  };
};

const normalizePriceDistribution = (value: unknown): MarketPriceDistribution | undefined => {
  if (!isRecord(value)) return undefined;
  const count = Math.max(0, Math.round(finiteNumber(value.count)));
  if (count === 0) return undefined;
  return {
    count,
    min: finiteNumber(value.min),
    q1: finiteNumber(value.q1),
    median: finiteNumber(value.median),
    q3: finiteNumber(value.q3),
    max: finiteNumber(value.max)
  };
};

const normalizeFunnelStage = (value: unknown): MarketFunnelStage => {
  const stage = isRecord(value) ? value : {};
  const percentile = typeof stage.subjectPricePercentile === "number" && Number.isFinite(stage.subjectPricePercentile)
    ? Math.max(0, Math.min(100, Math.round(stage.subjectPricePercentile)))
    : undefined;
  return {
    count: Math.max(0, Math.round(finiteNumber(stage.count))),
    priceDistribution: normalizePriceDistribution(stage.priceDistribution),
    subjectPricePercentile: percentile,
    subjectPositionText: textValue(stage.subjectPositionText) || undefined
  };
};

const normalizeMarketFunnel = (value: unknown): MarketFunnelSnapshot | undefined => {
  if (!isRecord(value) || !isRecord(value.stages)) return undefined;
  const rejectionReasonCounts = Object.fromEntries(MARKET_FUNNEL_REJECTION_REASONS.map((reason) => [
    reason,
    isRecord(value.rejectionReasonCounts)
      ? Math.max(0, Math.round(finiteNumber(value.rejectionReasonCounts[reason])))
      : 0
  ])) as Record<MarketFunnelRejectionReason, number>;
  const normalizeSamples = (samples: unknown) => {
    if (!isRecord(samples)) return {};
    return Object.fromEntries(MARKET_FUNNEL_REJECTION_REASONS.flatMap((reason) => {
      const items = samples[reason];
      if (!Array.isArray(items)) return [];
      return [[reason, items.flatMap((item) => {
        if (!isRecord(item)) return [];
        return [{
          id: textValue(item.id),
          title: textValue(item.title),
          area: positiveNumber(item.area),
          askingPrice: positiveNumber(item.askingPrice),
          reason
        }];
      }).slice(0, 5)]];
    }));
  };
  const stages = value.stages;
  return {
    capturedAt: dateValue(value.capturedAt, new Date().toISOString()),
    providerId: textValue(value.providerId),
    marketLabel: textValue(value.marketLabel),
    subjectArea: positiveNumber(value.subjectArea),
    subjectAreaBasis: value.subjectAreaBasis === "interior" || value.subjectAreaBasis === "apartment" || value.subjectAreaBasis === "total_only"
      ? value.subjectAreaBasis
      : "legacy_unspecified",
    subjectAskingPrice: positiveNumber(value.subjectAskingPrice),
    areaSimilarityMinimum: Math.max(0, Math.min(1, finiteNumber(value.areaSimilarityMinimum, 0.85))),
    areaBand: isRecord(value.areaBand) && positiveNumber(value.areaBand.min) && positiveNumber(value.areaBand.max)
      ? { min: positiveNumber(value.areaBand.min)!, max: positiveNumber(value.areaBand.max)! }
      : undefined,
    hardEligibleCount: Math.max(0, Math.round(finiteNumber(value.hardEligibleCount))),
    stages: {
      allMarket: normalizeFunnelStage(stages.allMarket),
      similarArea: normalizeFunnelStage(stages.similarArea),
      similarCondition: normalizeFunnelStage(stages.similarCondition),
      primaryComparables: normalizeFunnelStage(stages.primaryComparables),
      marketCompetition: normalizeFunnelStage(stages.marketCompetition),
      sameConditionMarket: isRecord(stages.sameConditionMarket) ? normalizeFunnelStage(stages.sameConditionMarket) : undefined,
      otherConditionAlternatives: isRecord(stages.otherConditionAlternatives) ? normalizeFunnelStage(stages.otherConditionAlternatives) : undefined
    },
    rejectionReasonCounts,
    rejectionSamples: normalizeSamples(value.rejectionSamples),
    cheaperSimilarAreaRejected: Array.isArray(value.cheaperSimilarAreaRejected)
      ? value.cheaperSimilarAreaRejected.flatMap((item) => {
        if (!isRecord(item) || !MARKET_FUNNEL_REJECTION_REASONS.includes(item.reason as MarketFunnelRejectionReason)) return [];
        return [{
          id: textValue(item.id),
          title: textValue(item.title),
          area: positiveNumber(item.area),
          askingPrice: positiveNumber(item.askingPrice),
          reason: item.reason as MarketFunnelRejectionReason
        }];
      }).slice(0, 5)
      : [],
    conditionStageAvailable: value.conditionStageAvailable === true
  };
};

const normalizeResult = (value: unknown): MarketValuationResult | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const now = new Date().toISOString();
  return {
    askingMarket: normalizeDataset(value.askingMarket),
    realizedMarket: normalizeDataset(value.realizedMarket),
    recommendedListingPrice: finiteNumber(value.recommendedListingPrice),
    marketLow: finiteNumber(value.marketLow),
    marketHigh: finiteNumber(value.marketHigh),
    marketMid: finiteNumber(value.marketMid) || Math.round((finiteNumber(value.marketLow) + finiteNumber(value.marketHigh)) / 2),
    ownerPricePosition: value.ownerPricePosition === "below" || value.ownerPricePosition === "within" || value.ownerPricePosition === "above"
      ? value.ownerPricePosition
      : "unknown",
    ownerDifferencePercent: typeof value.ownerDifferencePercent === "number" && Number.isFinite(value.ownerDifferencePercent)
      ? value.ownerDifferencePercent
      : undefined,
    ownerDifferenceAmount: typeof value.ownerDifferenceAmount === "number" && Number.isFinite(value.ownerDifferenceAmount)
      ? value.ownerDifferenceAmount
      : undefined,
    confidenceScore: Math.max(0, Math.min(100, finiteNumber(value.confidenceScore))),
    confidence: value.confidence === "high" || value.confidence === "medium" ? value.confidence : "low",
    methodologyVersion: textValue(value.methodologyVersion) || MARKET_METHODOLOGY_VERSION,
    calculatedAt: dateValue(value.calculatedAt, now)
  };
};

const normalizeStrategies = (value: unknown): MarketStrategy[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item) => {
    if (!isRecord(item) || !["quick-sale", "recommended", "market-test"].includes(String(item.kind))) {
      return [];
    }

    return [{
      kind: item.kind as MarketStrategy["kind"],
      title: textValue(item.title),
      listingPrice: finiteNumber(item.listingPrice),
      explanation: textValue(item.explanation)
    }];
  });
};

export const normalizeMarketAnalyses = (value: unknown): MarketAnalysis[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item) => {
    if (!isRecord(item) || typeof item.id !== "string" || typeof item.caseId !== "string") {
      return [];
    }

    const now = new Date().toISOString();
    const userId = textValue(item.userId) || "local";
    const analysisId = item.id;
    const comparables = Array.isArray(item.comparables)
      ? item.comparables
          .map((comparable, index) => normalizeComparable(comparable, analysisId, userId, index))
          .filter((comparable): comparable is MarketAnalysisComparable => Boolean(comparable))
          .sort((first, second) => first.sortOrder - second.sortOrder)
      : [];

    return [{
      id: analysisId,
      userId,
      caseId: item.caseId,
      version: Math.max(1, Math.round(finiteNumber(item.version, 1))),
      status: item.status === "finalized" ? "finalized" : "draft",
      subjectSnapshot: normalizeMarketSubject(item.subjectSnapshot),
      comparables,
      marketFunnelSnapshot: normalizeMarketFunnel(item.marketFunnelSnapshot),
      resultSnapshot: normalizeResult(item.resultSnapshot),
      strategySnapshot: normalizeStrategies(item.strategySnapshot),
      methodologyVersion: textValue(item.methodologyVersion) || MARKET_METHODOLOGY_VERSION,
      sourceCutoffAt: dateValue(item.sourceCutoffAt, now),
      snapshotHash: textValue(item.snapshotHash) || undefined,
      createdAt: dateValue(item.createdAt, now),
      updatedAt: dateValue(item.updatedAt, now),
      finalizedAt: typeof item.finalizedAt === "string" ? dateValue(item.finalizedAt, now) : undefined
    }];
  });
};

export const createSubjectFromCase = (caseItem: RealEstateCase): MarketSubjectSnapshot => {
  const addressParts = caseItem.propertyAddress.split(",").map((part) => part.trim()).filter(Boolean);
  const legacyCity = addressParts.length > 1 ? addressParts[addressParts.length - 1] ?? "" : "";
  const legacyStreet = addressParts.length > 1
    ? addressParts.slice(0, -1).join(", ")
    : caseItem.propertyAddress;
  const city = caseItem.city ?? legacyCity;
  const street = caseItem.street ?? legacyStreet;
  return {
    city,
    cityNormalized: caseItem.cityNormalized ?? normalizeLocationText(city),
    citySource: caseItem.citySource ?? (city ? "custom" : undefined),
    municipalityId: caseItem.municipalityId,
    locality: caseItem.locality ?? "",
    street,
    streetNormalized: caseItem.streetNormalized ?? normalizeLocationText(street),
    streetSource: caseItem.streetSource ?? (street ? "custom" : undefined),
    streetId: caseItem.streetId,
    propertyType: caseItem.propertyType,
    rooms: caseItem.apartmentRooms ?? "",
    area: caseItem.area,
    condition: "",
    reconstruction: "",
    electricity: "",
    coreType: "",
    kitchen: "",
    balconyType: "",
    cellar: "",
    floor: undefined,
    elevator: "",
    orientation: "",
    buildingCondition: "",
    buildingType: "",
    location: {
      municipality: city,
      municipalityNormalized: caseItem.cityNormalized ?? normalizeSlovakLocationName(city),
      municipalityId: caseItem.municipalityId,
      locality: caseItem.locality,
      street,
      address: caseItem.propertyAddress
    },
    areas: caseItem.area ? { totalArea: caseItem.area, areaBasis: "legacy_unspecified" } : undefined,
    canonicalPropertyType: canonicalPropertyType(caseItem.propertyType),
    technical: { condition: "unknown" },
    fieldProvenance: caseItem.area ? {
      totalArea: {
        source: "FISPRO Case · historická výmera bez určeného základu",
        confidence: "low",
        capturedAt: caseItem.updatedAt,
        rawValue: caseItem.area
      }
    } : undefined,
    transactionType: caseItem.transactionType,
    askingPriceAtAnalysis: caseItem.askingPrice > 0 ? caseItem.askingPrice : undefined
  };
};

export const validateSubject = (subject: MarketSubjectSnapshot): string[] => {
  const errors: string[] = [];
  if (!subject.city.trim()) errors.push("Doplň mesto.");
  if (!subject.propertyType.trim()) errors.push("Vyber typ nehnuteľnosti.");
  if (canonicalPropertyType(subject.propertyType) === "apartment" && normalizeRoomCount(subject.rooms) === undefined) errors.push("Vyber platný počet izieb.");
  if (!isPlausibleApartmentArea(effectiveApartmentArea(subject).value)) errors.push("Doplň platnú výmeru bytu.");
  return errors;
};

export const validateComparable = (comparable: MarketComparableDraft): string[] => {
  const errors: string[] = [];
  if (!comparable.city.trim()) errors.push("Doplň mesto porovnateľnej nehnuteľnosti.");
  const area = effectiveApartmentArea(comparable).value;
  if (!isPlausibleApartmentArea(area)) errors.push("Doplň platnú výmeru comparable.");
  const price = comparable.realizedPrice ?? comparable.askingPrice;
  if (!isPlausibleMarketPrice(price) || !isPlausiblePricePerM2(price, area)) errors.push("Doplň použiteľnú trhovú cenu.");
  if (!Number.isFinite(Date.parse(comparable.capturedAt))) errors.push("Doplň platný dátum zachytenia.");
  return errors;
};

export const validateFinalization = (analysis: MarketAnalysis): string[] => {
  const errors = validateSubject(analysis.subjectSnapshot);
  if (!analysis.comparables.some((comparable) => comparable.decision !== "excluded")) errors.push("Použi aspoň jednu porovnateľnú nehnuteľnosť.");
  if (!analysis.resultSnapshot) errors.push("Najprv vypočítaj výsledok analýzy.");
  if (analysis.strategySnapshot.length !== 3) errors.push("Analýza nemá pripravené tri cenové stratégie.");
  return errors;
};

const stableStringify = (value: unknown): string => {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }
  if (isRecord(value)) {
    return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(",")}}`;
  }
  return JSON.stringify(value) ?? "null";
};

export const snapshotHash = (value: unknown) => {
  const source = stableStringify(value);
  let first = 0x811c9dc5;
  let second = 0x9e3779b9;
  for (let index = 0; index < source.length; index += 1) {
    const code = source.charCodeAt(index);
    first = Math.imul(first ^ code, 0x01000193);
    second = Math.imul(second ^ code, 0x85ebca6b);
  }
  return `${(first >>> 0).toString(16).padStart(8, "0")}${(second >>> 0).toString(16).padStart(8, "0")}`;
};
